import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getFlexMetricsDateRange } from 'modules/workflows/ducks';
import { NODE_TYPES } from 'modules/workflows/constants';
import {
  BackendJourneyConversion,
  ChildNode,
  Node,
  NodeConfig,
  Workflow,
} from 'modules/workflows/types';
import { getBaseNodeConfig, sendNodeUtils } from 'modules/workflows/utils';
import { WorkflowType } from 'modules/workflows/enums';
import { useGetJourneyConversionsMetricsForCampaigns } from 'modules/journey/hooks/useGetJourneyConversionsMetricsForCampaigns/useGetJourneyConversionsMetricsForCampaigns';
import { ContactCounts } from '../ContactCounts';
import { InsightsButton } from '../InsightsButton';
import {
  IconContainer,
  StyledSelectedTileTitleText,
  SelectedTilePanel,
  SelectedTileHeader,
  StyledSpinner,
  InsightsButtonContainer,
} from '../styled';
import { getAnalyticsLink } from './NodeAnalyticsCard.utils';
import { ConversionCounts } from '../ContactCounts/ConversionCounts';
import { getNodesCampaignsIds } from '../WorkflowAnalytics.utils';

export enum BaseCardType {
  Analytics = 'analytics',
  Conversions = 'conversions',
}

export const getNodeCardTestIds = (cardType: BaseCardType) => ({
  CONTAINER: `node-${cardType}-card-container`,
  TITLE: `node-${cardType}-card-title`,
  ICON: `node-${cardType}-card-icon`,
  LOADING_SPINNER: `node-${cardType}-card-spinner`,
});

interface BaseNodeCardProps {
  /** Card data loading */
  isLoading?: boolean;
  /** The card type */
  cardType: BaseCardType;
  /** The current workflow */
  workflow: Workflow;
  /** Selected node */
  node: Node;
  /** Selected node */
  content?: React.ReactNode;
  /** Selected node */
  footer?: React.ReactNode;
}

export const BaseNodeCard = ({
  isLoading,
  cardType,
  workflow,
  node,
  content,
  footer,
}: BaseNodeCardProps) => {
  const testIds = getNodeCardTestIds(cardType);
  const { type, title, defaultTitle } = node;
  const baseConfig = useMemo(() => (type && getBaseNodeConfig(type)) || ({} as NodeConfig), [type]);
  const { accentColor, icon: Icon } = baseConfig;

  return (
    <SelectedTilePanel data-test={testIds.CONTAINER}>
      <SelectedTileHeader>
        {Icon && (
          <IconContainer>
            <Icon data-test={testIds.ICON} data-color={accentColor} color={accentColor} />
          </IconContainer>
        )}{' '}
        <StyledSelectedTileTitleText data-test={testIds.TITLE}>
          {title || defaultTitle}
        </StyledSelectedTileTitleText>
      </SelectedTileHeader>
      {isLoading ? <StyledSpinner data-test={testIds.LOADING_SPINNER} /> : workflow?.id && content}
      {footer && footer}
    </SelectedTilePanel>
  );
};

interface NodeConversionsCardProps extends Pick<BaseNodeCardProps, 'node' | 'workflow'> {
  /** Journey conversion for the journey  */
  journeyConversion: BackendJourneyConversion;
}

export const NodeConversionsCard = ({
  workflow,
  node,
  journeyConversion,
}: NodeConversionsCardProps) => {
  const flexRange = useSelector(getFlexMetricsDateRange);
  const { endDateString, startDateString } = flexRange;
  const { getJourneyConversionsMetricsForCampaigns, isLoading, journeyConversionData } =
    useGetJourneyConversionsMetricsForCampaigns();
  const conversionEventName = journeyConversion?.conversions?.[0]?.eventName;
  const campaignIds = getNodesCampaignsIds([node]);

  useEffect(() => {
    if (workflow?.id) {
      getJourneyConversionsMetricsForCampaigns(
        workflow.id,
        campaignIds,
        startDateString as string,
        endDateString as string,
      );
    }
  }, [workflow?.id, startDateString, endDateString]);

  const conversionValue = journeyConversionData?.[conversionEventName];

  return (
    <BaseNodeCard
      isLoading={isLoading}
      cardType={BaseCardType.Conversions}
      workflow={workflow}
      node={node}
      content={<ConversionCounts totalConversion={conversionValue} />}
    />
  );
};

interface NodeAnalyticsCardProps extends Pick<BaseNodeCardProps, 'node' | 'workflow'> {
  /** Should the view insights links be shown? */
  showViewInsightsLinks?: boolean;
}

export const NodeAnalyticsCard = ({
  workflow,
  showViewInsightsLinks = false,
  node,
}: NodeAnalyticsCardProps) => {
  const dateRange = useSelector(getFlexMetricsDateRange);
  const insightsButtons = useMemo(() => {
    if (!showViewInsightsLinks) return null;
    if (!sendNodeUtils.isSendNode(node?.type)) return null;
    if (node?.type === NODE_TYPES.CHANNEL_OPT) {
      const campaignNodes = node?.nodeData?.childNodes || [];
      return (
        <>
          {campaignNodes.map((n: ChildNode, i: number) => (
            <InsightsButton key={i} to={getAnalyticsLink(n?.nodeData?.campaignId, dateRange)}>
              View {(n.defaultTitle || '').toLowerCase()} insights
            </InsightsButton>
          ))}
        </>
      );
    }
    return (
      <InsightsButtonContainer>
        <InsightsButton to={getAnalyticsLink(node?.nodeData?.campaignId, dateRange)}>
          Campaign analytics
        </InsightsButton>
      </InsightsButtonContainer>
    );
  }, [showViewInsightsLinks, node, workflow, dateRange]);

  return (
    <BaseNodeCard
      cardType={BaseCardType.Analytics}
      workflow={workflow}
      node={node}
      content={
        <ContactCounts
          workflowId={workflow.id}
          workflowType={workflow.workflowType as WorkflowType}
          selectedNode={node}
          openJourneyVisitorBreakdown={() => ({}) as () => void}
        />
      }
      footer={insightsButtons}
    />
  );
};
